export const ENTITY_WORLD_VERSION = 1;

const DEFAULT_ID_PREFIX = 'entity';

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, canonicalize(value[key])]),
  );
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${name} must be a plain object`);
  }
  return value;
}

function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
  return value.trim();
}

function assertFinite(value, name) {
  if (!Number.isFinite(value)) throw new RangeError(`${name} must be finite`);
  return value;
}

function assertFiniteNonNegative(value, name) {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative finite number`);
  }
  return value;
}

function assertPositiveFinite(value, name) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive finite number`);
  }
  return value;
}

function assertNonNegativeInteger(value, name) {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
  return value;
}

function assertPositiveInteger(value, name) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer`);
  }
  return value;
}

function validateJsonValue(value, name, seen = new WeakSet()) {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new RangeError(`${name} contains a non-finite number`);
    return;
  }
  if (typeof value !== 'object') {
    throw new TypeError(`${name} must contain JSON-compatible values`);
  }
  if (seen.has(value)) throw new TypeError(`${name} must not contain circular references`);
  seen.add(value);
  if (Array.isArray(value)) {
    value.forEach((entry, index) => validateJsonValue(entry, `${name}[${index}]`, seen));
  } else {
    assertPlainObject(value, name);
    for (const [key, entry] of Object.entries(value)) {
      validateJsonValue(entry, `${name}.${key}`, seen);
    }
  }
  seen.delete(value);
}

function normalizePosition(position = {}, fallback = { x: 0, y: 0, z: 0 }) {
  assertPlainObject(position, 'position');
  return {
    x: assertFinite(position.x ?? fallback.x, 'position.x'),
    y: assertFinite(position.y ?? fallback.y, 'position.y'),
    z: assertFinite(position.z ?? fallback.z, 'position.z'),
  };
}

function normalizeTags(tags = []) {
  if (!Array.isArray(tags)) throw new TypeError('tags must be an array');
  return [...new Set(tags.map((tag, index) => assertNonEmptyString(tag, `tags[${index}]`)))].sort();
}

function normalizeComponents(components = {}) {
  assertPlainObject(components, 'components');
  validateJsonValue(components, 'components');
  return clone(components);
}

function normalizeEntity(entity, { defaultId, worldRevision, existing = null } = {}) {
  assertPlainObject(entity, 'entity');
  const id = assertNonEmptyString(entity.id ?? defaultId, 'entity.id');
  const type = existing
    ? existing.type
    : assertNonEmptyString(entity.type, 'entity.type');
  if (existing && entity.type !== undefined && entity.type !== existing.type) {
    throw new RangeError('entity.type is immutable');
  }
  const position = entity.position === undefined
    ? clone(existing?.position ?? { x: 0, y: 0, z: 0 })
    : normalizePosition(entity.position, existing?.position);
  const tags = entity.tags === undefined
    ? clone(existing?.tags ?? [])
    : normalizeTags(entity.tags);
  const components = entity.components === undefined
    ? clone(existing?.components ?? {})
    : normalizeComponents(entity.components);
  const revision = entity.revision === undefined
    ? (existing?.revision ?? 0) + 1
    : assertPositiveInteger(entity.revision, 'entity.revision');
  const entityWorldRevision = entity.worldRevision === undefined
    ? worldRevision
    : assertNonNegativeInteger(entity.worldRevision, 'entity.worldRevision');

  return deepFreeze({
    id,
    type,
    position,
    tags,
    components,
    revision,
    worldRevision: entityWorldRevision,
  });
}

function checksumPayload(payload) {
  const serialized = JSON.stringify(canonicalize(payload));
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function computeEntityWorldChecksum(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') {
    throw new TypeError('snapshot must be an object');
  }
  const payload = clone(snapshot);
  delete payload.checksum;
  return checksumPayload(payload);
}

function normalizeLimit(limit) {
  if (limit === undefined) return Infinity;
  return assertPositiveInteger(limit, 'limit');
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function nextNumberAfterExplicitId(id, idPrefix, current) {
  const match = id.match(new RegExp(`^${escapeRegExp(idPrefix)}-(\\d+)$`));
  return match ? Math.max(current, Number(match[1]) + 1) : current;
}

export class EntityWorld {
  constructor({ idPrefix = DEFAULT_ID_PREFIX } = {}) {
    this.idPrefix = assertNonEmptyString(idPrefix, 'idPrefix');
    this.entities = new Map();
    this.revision = 0;
    this.nextEntityNumber = 1;
    this.currentStep = {
      tick: null,
      simulationTime: 0,
      stepSeconds: null,
    };
  }

  #nextGeneratedId() {
    let candidate = this.nextEntityNumber;
    let id;
    do {
      id = `${this.idPrefix}-${String(candidate).padStart(6, '0')}`;
      candidate += 1;
    } while (this.entities.has(id));
    return { id, nextEntityNumber: candidate };
  }

  spawn(definition) {
    assertPlainObject(definition, 'definition');
    if (definition.revision !== undefined || definition.worldRevision !== undefined) {
      throw new RangeError('entity revision fields are authoritative');
    }
    const generated = definition.id === undefined ? this.#nextGeneratedId() : null;
    const id = generated
      ? generated.id
      : assertNonEmptyString(definition.id, 'definition.id');
    if (this.entities.has(id)) throw new RangeError(`Entity already exists: ${id}`);
    const nextRevision = this.revision + 1;
    const nextEntityNumber = generated
      ? generated.nextEntityNumber
      : nextNumberAfterExplicitId(id, this.idPrefix, this.nextEntityNumber);
    const entity = normalizeEntity({ ...definition, id }, {
      defaultId: id,
      worldRevision: nextRevision,
    });

    this.revision = nextRevision;
    this.nextEntityNumber = nextEntityNumber;
    this.entities.set(id, entity);
    return entity;
  }

  update(id, patch) {
    const normalizedId = assertNonEmptyString(id, 'id');
    const existing = this.entities.get(normalizedId);
    if (!existing) throw new Error(`Unknown entity: ${normalizedId}`);
    assertPlainObject(patch, 'patch');
    if (patch.id !== undefined && patch.id !== normalizedId) {
      throw new RangeError('entity.id is immutable');
    }
    if (patch.revision !== undefined || patch.worldRevision !== undefined) {
      throw new RangeError('entity revision fields are authoritative');
    }
    const nextRevision = this.revision + 1;
    const updated = normalizeEntity({ ...patch, id: normalizedId }, {
      worldRevision: nextRevision,
      existing,
    });

    this.revision = nextRevision;
    this.entities.set(normalizedId, updated);
    return updated;
  }

  remove(id) {
    const normalizedId = assertNonEmptyString(id, 'id');
    const existing = this.entities.get(normalizedId);
    if (!existing) return null;
    this.entities.delete(normalizedId);
    this.revision += 1;
    return existing;
  }

  has(id) {
    return this.entities.has(assertNonEmptyString(id, 'id'));
  }

  get(id) {
    return this.entities.get(assertNonEmptyString(id, 'id')) ?? null;
  }

  list({ type = undefined, tags = [], limit = undefined } = {}) {
    const normalizedType = type === undefined ? null : assertNonEmptyString(type, 'type');
    const normalizedTags = normalizeTags(tags);
    const normalizedLimit = normalizeLimit(limit);
    return [...this.entities.values()]
      .filter((entity) => normalizedType === null || entity.type === normalizedType)
      .filter((entity) => normalizedTags.every((tag) => entity.tags.includes(tag)))
      .sort((left, right) => left.id.localeCompare(right.id))
      .slice(0, normalizedLimit);
  }

  query({
    type = undefined,
    tags = [],
    center = undefined,
    radius = undefined,
    limit = undefined,
  } = {}) {
    if ((center === undefined) !== (radius === undefined)) {
      throw new TypeError('center and radius must be provided together');
    }
    const normalizedCenter = center === undefined ? null : normalizePosition(center);
    const normalizedRadius = radius === undefined ? null : assertFiniteNonNegative(radius, 'radius');
    const normalizedLimit = normalizeLimit(limit);
    const candidates = this.list({ type, tags });
    if (!normalizedCenter) return candidates.slice(0, normalizedLimit);
    const radiusSquared = normalizedRadius ** 2;
    return candidates
      .map((entity) => ({
        entity,
        distanceSquared:
          ((entity.position.x - normalizedCenter.x) ** 2)
          + ((entity.position.y - normalizedCenter.y) ** 2)
          + ((entity.position.z - normalizedCenter.z) ** 2),
      }))
      .filter((entry) => entry.distanceSquared <= radiusSquared)
      .sort((left, right) => (
        left.distanceSquared - right.distanceSquared
        || left.entity.id.localeCompare(right.entity.id)
      ))
      .slice(0, normalizedLimit)
      .map((entry) => entry.entity);
  }

  beginStep({ tick, simulationTime, stepSeconds }) {
    const nextStep = {
      tick: assertNonNegativeInteger(tick, 'tick'),
      simulationTime: assertFiniteNonNegative(simulationTime, 'simulationTime'),
      stepSeconds: assertPositiveFinite(stepSeconds, 'stepSeconds'),
    };
    this.currentStep = nextStep;
    return this.getStateRef();
  }

  snapshot() {
    const payload = {
      version: ENTITY_WORLD_VERSION,
      idPrefix: this.idPrefix,
      revision: this.revision,
      nextEntityNumber: this.nextEntityNumber,
      tick: this.currentStep.tick,
      simulationTime: this.currentStep.simulationTime,
      stepSeconds: this.currentStep.stepSeconds,
      entityCount: this.entities.size,
      entities: this.list().map((entity) => clone(entity)),
    };
    return deepFreeze({
      ...payload,
      checksum: computeEntityWorldChecksum(payload),
    });
  }

  getStateRef() {
    const snapshot = this.snapshot();
    return deepFreeze({
      version: snapshot.version,
      revision: snapshot.revision,
      tick: snapshot.tick,
      entityCount: snapshot.entityCount,
      checksum: snapshot.checksum,
    });
  }

  restore(snapshot) {
    const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    assertPlainObject(parsed, 'snapshot');
    if (parsed.version !== ENTITY_WORLD_VERSION) {
      throw new RangeError(`Unsupported entity world version: ${parsed.version}`);
    }
    const idPrefix = assertNonEmptyString(parsed.idPrefix, 'snapshot.idPrefix');
    const revision = assertNonNegativeInteger(parsed.revision, 'snapshot.revision');
    const nextEntityNumber = assertPositiveInteger(parsed.nextEntityNumber, 'snapshot.nextEntityNumber');
    const tick = parsed.tick === null ? null : assertNonNegativeInteger(parsed.tick, 'snapshot.tick');
    const simulationTime = assertFiniteNonNegative(parsed.simulationTime, 'snapshot.simulationTime');
    const stepSeconds = parsed.stepSeconds === null
      ? null
      : assertPositiveFinite(parsed.stepSeconds, 'snapshot.stepSeconds');
    if (!Array.isArray(parsed.entities)) throw new TypeError('snapshot.entities must be an array');
    if (parsed.entityCount !== parsed.entities.length) {
      throw new RangeError('snapshot.entityCount must match snapshot.entities.length');
    }

    const normalizedEntities = parsed.entities.map((entry) => normalizeEntity(entry, {
      worldRevision: entry.worldRevision,
    })).sort((left, right) => left.id.localeCompare(right.id));
    const ids = new Set(normalizedEntities.map((entity) => entity.id));
    if (ids.size !== normalizedEntities.length) throw new RangeError('snapshot contains duplicate entity IDs');
    if (normalizedEntities.some((entity) => entity.worldRevision > revision)) {
      throw new RangeError('entity.worldRevision cannot exceed snapshot.revision');
    }

    const payload = {
      version: ENTITY_WORLD_VERSION,
      idPrefix,
      revision,
      nextEntityNumber,
      tick,
      simulationTime,
      stepSeconds,
      entityCount: normalizedEntities.length,
      entities: normalizedEntities.map((entity) => clone(entity)),
    };
    const expectedChecksum = computeEntityWorldChecksum(payload);
    if (typeof parsed.checksum !== 'string' || parsed.checksum !== expectedChecksum) {
      throw new RangeError('snapshot checksum is invalid');
    }

    this.idPrefix = idPrefix;
    this.revision = revision;
    this.nextEntityNumber = nextEntityNumber;
    this.currentStep = { tick, simulationTime, stepSeconds };
    this.entities = new Map(normalizedEntities.map((entity) => [entity.id, entity]));
    return this.snapshot();
  }

  reset({ nextEntityNumber = 1 } = {}) {
    const normalizedNextEntityNumber = assertPositiveInteger(nextEntityNumber, 'nextEntityNumber');
    this.entities = new Map();
    this.revision = 0;
    this.nextEntityNumber = normalizedNextEntityNumber;
    this.currentStep = {
      tick: null,
      simulationTime: 0,
      stepSeconds: null,
    };
    return this.getStatus();
  }

  getStatus() {
    return deepFreeze({
      version: ENTITY_WORLD_VERSION,
      idPrefix: this.idPrefix,
      revision: this.revision,
      nextEntityNumber: this.nextEntityNumber,
      tick: this.currentStep.tick,
      simulationTime: this.currentStep.simulationTime,
      stepSeconds: this.currentStep.stepSeconds,
      entityCount: this.entities.size,
    });
  }
}
